// Prototype. Do not instantiate, but extend this class in order to create custom filters.
// https://www.ag-grid.com/angular-data-grid/component-filter/

import { IFilterAngularComp } from 'ag-grid-angular';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';
import { Component } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface CustomFilterParams extends IFilterParams {
  // dropdown options
  filterOptions: { name, value }[] | Observable<{ name, value }[]>;
  // whether dropdown options are searchable
  searchFieldHidden?: boolean;
  // these strings will always pass the filter condition no matter what filter dropdown value is
  // selected
  validStrings?: string[];
  // define custom filter logic => implemented only for single select and chip list filter
  filterCb?: (filterValue: any, cellValue: any) => boolean
  gridContainerId?: string; // used to compute max dropdown height of multiselect filters
  minWidthPx?: number;
}

@Component({
  template: '',
})
export abstract class AgFilterComponent implements IFilterAngularComp {

  public params: CustomFilterParams;
  // filter state - might be in a different form than filterValue, for instance a FormControl
  protected filterState: any;

  // active filter values that the grid should use to filter rows
  get filterValue(): string | string[] | boolean | { from: string, to: string } {
    return this.filterState;
  };

  protected componentDestroyed = new Subject<void>();

  /**
   * Mandatory - params for filter operations
   * @param params
   */
  agInit(params: CustomFilterParams): void {
    this.params = params;
  }

  /**
   * Return true if the filter is active. If active then 1) the grid will show the filter icon in the column
   * header and 2) the filter will be included in the filtering of the data.
   */
  isFilterActive(): boolean {
    return (
      // empty array check
      (this.filterValue !== null &&
      this.filterValue !== undefined &&
      this.filterValue !== '' && !(Array.isArray(this.filterValue) && !this.filterValue.length))
    );
  }

  /**
   * The grid will ask each active filter, in turn, whether each row in the grid passes. If any
   * filter fails, then the row will be excluded from the final set. A params object is supplied
   * containing attributes of node (the rowNode the grid creates that wraps the data) and data (the data
   * object that you provided to the grid for that row).
   * @param params
   */
  doesFilterPass(params: IDoesFilterPassParams): boolean {
    throw new Error('Method not implemented (override me).');
  }

  /**
   * Gets the filter state. If filter is not active, then should return null/undefined.
   * The grid calls getModel() on all active filters when gridApi.getFilterModel() is called.
   */
  getModel(): string | string[] | boolean | { from: string, to: string } {
    return this.isFilterActive() ? this.filterValue : null;
  }

  /**
   * Restores the filter state. Called by the grid after gridApi.setFilterModel(model) is called.
   * The grid will pass undefined/null to clear the filter.
   * @param model
   */
  setModel(model: string | string[] | boolean | { from: string, to: string}) {
    this.filterState = model;
    this.updateFilter();
  }

  /**
   * Apply filter value
   */
  updateFilter() {
    this.params.filterChangedCallback();
  }

  // Gets called when the column is destroyed. If your custom filter needs to do
  // any resource cleaning up, do it here. A filter is NOT destroyed when it is
  // made 'not visible', as the GUI is kept to be shown again if the user selects
  // that filter again. The filter is destroyed when the column it is associated with is
  // destroyed, either when new columns are set into the grid, or the grid itself is destroyed.
  destroy(): void {
    this.componentDestroyed.next();
  };
}
